import React, { FC, forwardRef, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import {
  HeaderCountryPickerOption,
  HeaderCountryPickerOptionContent,
  TabContent,
} from 'components/Header/components/styled';
import { ContentfulCountry } from 'types/common';
import { makeSCUrlFromLocale } from 'services/localization';
import { SELECTED_COUNTRY } from 'constants/countriesDetails';
import { ICONS_BASE_URL } from 'constants/common';

type CountryOptionsProps = {
  countriesList: Array<ContentfulCountry>;
  handleCountryClick: (el: ContentfulCountry) => void;
  currentCountry: string;
  enabledLocales: string[];
  removeModal?: () => void;
};

const CountryOptions: FC<CountryOptionsProps> = ({
  countriesList,
  currentCountry,
  enabledLocales,
  handleCountryClick,
  removeModal,
}) => {
  const router = useRouter();

  const BaseComponent = useMemo(
    () =>
      // eslint-disable-next-line prefer-arrow-callback
      forwardRef(function CustomComponent(
        {
          el,
          href,
          onClick,
        }: {
          el: ContentfulCountry;
          href?: string;
          onClick?: () => void;
        },
        /* eslint-disable @typescript-eslint/no-unused-vars */
        _ref,
      ) {
        const countryCode = el.twoLetterIsoCode?.toLowerCase();
        return (
          <HeaderCountryPickerOption
            key={el.name}
            className={
              el.twoLetterIsoCode === currentCountry ? SELECTED_COUNTRY : null
            }
            onClick={onClick}
            href={href}
          >
            <HeaderCountryPickerOptionContent>
              {countryCode && (
                <img
                  alt=""
                  src={`${ICONS_BASE_URL}/flag_${countryCode}_16.svg`}
                  loading="lazy"
                />
              )}
              {el.name}
            </HeaderCountryPickerOptionContent>
          </HeaderCountryPickerOption>
        );
      }),
    [currentCountry],
  );

  return (
    <TabContent>
      {countriesList.map((el) => {
        const scURL = makeSCUrlFromLocale({
          language: el?.supportedLanguages?.[0]?.fields?.twoLetterIsoCode,
          country: el?.twoLetterIsoCode,
          oldSCURL: el?.defaultZendeskSupportCentre,
          enabledLocales,
        });

        if (!scURL.locale) {
          return null;
        }

        const linkConfig: {
          href: string;
          locale: false | string;
        } = scURL.locale
          ? {
              href: router.asPath,
              locale: scURL.locale,
            }
          : {
              href: el.defaultZendeskSupportCentre,
              locale: false,
            };

        return el?.supportedLanguages && el.supportedLanguages.length < 2 ? (
          <Link key={el.name} {...linkConfig} passHref legacyBehavior>
            <BaseComponent onClick={removeModal} key={el.name} el={el} />
          </Link>
        ) : (
          <BaseComponent
            key={el.name}
            el={el}
            onClick={() => handleCountryClick(el)}
          />
        );
      })}
    </TabContent>
  );
};

export default CountryOptions;
